import coreTheme from '@hellodarwin/core/web/theme';
import { css } from 'styled-components';

const theme = {
  ...coreTheme,
  fonts: {
    ...coreTheme.fonts,

    blockquote: '20px',
    blockquote_md: '18px',

    link_l: '20px', // min 1200px
    link_md: '20px', // min 960px
    link: '18px', // min 0
  },
  fontsLineHeights: {
    ...coreTheme.fontsLineHeights,

    blockquote: '28px',
    blockquote_md: '26px',

    link_l: '0', // min 1200px
    link_md: '0', // min 960px
    link: '0', // min 0
  },
  letterSpacing: {
    ...coreTheme.letterSpacing,

    blockquote: 0,
    blockquote_md: 0,

    link_l: 0,
    link_md: 0,
    link: 0,
  },
  caps: {
    small: 960,
    normal: 1120,
    big: 1260,
    xl: 1440,
    auto: 'auto',
  },
  appearance: {
    ...coreTheme.appearance,
    headerHeight: 73,
    dynamicSliderHeight: 66 * 2 + 32,
    bannerHeight: 83,
  },
};

export default theme;

export type WebsiteTheme = typeof theme;

declare module 'styled-components' {
  export interface DefaultTheme extends WebsiteTheme {}
}

export const anchor = css`
  color: ${({ theme }) => theme.colors.purple_2};
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colors.purple_1};
    text-decoration: none;
  }
  &:active {
    color: ${({ theme }) => theme.colors.purple_3};
  }
  text-underline-offset: 8px;
`;
export const li = css`
  padding-inline: 4px 0;
  margin-inline: 24px 0;
  margin-block: 4px 0;
`;
export const ul = css`
  list-style-position: outside;
  margin-block: 0;
  * {
    vertical-align: top;
  }
`;
export const ol = css`
  list-style-position: outside;
  margin-block: 0;
  * {
    vertical-align: top;
  }
`;

export const p = css`
  font-size: ${({ theme }) => theme?.fonts?.body1};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.body1};
  font-weight: 400;
  font-style: normal;
  line-height: 1.55;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}px) {
    font-size: ${({ theme }) => theme?.fonts?.body1_md};
    letter-spacing: ${({ theme }) => theme?.letterSpacing?.body1_md};
  }
`;

export const small = css`
  font-size: ${({ theme }) => theme?.fonts?.body3};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.body3};
  line-height: 1.5;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}px) {
    font-size: ${({ theme }) => theme?.fonts?.body3_md};
    letter-spacing: ${({ theme }) => theme?.letterSpacing?.body3_md};
  }
`;

export const blockquote = css`
  font-size: ${({ theme }) => theme?.fonts?.blockquote};
  line-height: ${({ theme }) => theme?.fontsLineHeights?.blockquote};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.blockquote};
  font-style: italic;
  font-weight: ${({ theme }) => theme?.fontWeights?.regular};
  color: ${({ theme }) => theme?.colors?.grey_1};
  border-left: 4px solid ${({ theme }) => theme?.colors?.yellow_1};
  padding-left: 1.5rem;
  margin: 1.5rem 0;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}px) {
    font-size: ${({ theme }) => theme?.fonts?.blockquote_md};
    line-height: ${({ theme }) => theme?.fontsLineHeights?.blockquote_md};
    letter-spacing: ${({ theme }) => theme?.letterSpacing?.blockquote_md};
  }
`;

export const h1 = css`
  font-family: Figtree;
  font-size: ${({ theme }) => theme?.fonts?.h1};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.h1};
  font-weight: 700;
  line-height: 1.09;

  * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;
export const h2 = css`
  font-family: 'Figtree';
  font-size: ${({ theme }) => theme?.fonts?.h2};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.h2};
  font-weight: 700;
  line-height: 1.3;

  * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;
export const h3 = css`
  font-family: Figtree;
  font-size: ${({ theme }) => theme?.fonts?.h3};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.h3};
  font-weight: 600;
  line-height: 1;

  * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;
export const h4 = css`
  font-family: Figtree;
  font-size: ${({ theme }) => theme?.fonts?.h4};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.h4};
  font-weight: 600;
  line-height: 1;

  * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;
export const h5 = css`
  font-size: ${({ theme }) => theme?.fonts?.h5};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.h5};
  font-weight: 500;
  line-height: 1;
  font-family: Figtree;

  * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;

export const h6 = css`
  font-family: Figtree;
  font-size: ${({ theme }) => theme?.fonts?.h6};
  letter-spacing: ${({ theme }) => theme?.letterSpacing?.h6};
  font-weight: 500;
  line-height: 1;

  * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}px) {
    font-size: ${({ theme }) => theme?.fonts?.h6_md};
    letter-spacing: ${({ theme }) => theme?.letterSpacing?.h6_md};
    line-height: 1.4;
  }
`;

export const defaultTheme = css`
  h1 {
    ${h1};
  }

  h2 {
    ${h2};
  }

  h3 {
    ${h3};
  }

  h4 {
    ${h4}
  }

  h5 {
    ${h5};
  }

  p,
  input {
    ${p};
  }

  p {
    display: block;
  }

  small {
    ${small};
  }

  blockquote {
    ${blockquote};
  }

  a {
    color: inherit;
    &:hover {
      color: inherit;
    }
    text-decoration: none;
  }

  ul,
  ol {
    list-style-position: outside;
    margin-block: 12px;
    margin-inline-start: 24px;
    li {
      margin-block: 6px;
      padding-inline-start: 8px;
      & > * {
        display: inline;
      }
    }
  }
`;
